import { observer } from "mobx-react";
import { Box, VBox } from "./Box";
import { Icon } from "./Icon";
import { classNames } from "@/utils/classNames";
import { BetaLabel } from "./BetaLabel";
import { ButtonHTMLAttributes, forwardRef, ReactElement, useCallback, useState } from "react";
import { DropdownMenu, DropdownMenuItemType } from "./Dropdown";
import { formatOrgNum } from "@/utils/other";
import { Tag } from "./Tag";
import { Button, IconButton, IconButtonProps } from "./Button";
import { accessColors, AccessType } from "@/types/enums/AccessType";
import { isEmpty, noop } from "lodash";
import { isMobileView } from "@/utils/screen";
import { TabElementProps, Tabs } from "./Tabs";
import styles from "./AppBar.module.css";
import {
  accessTagLabel,
  AppBarData,
  AppBarLink,
  AppBarSubLink,
  CompanyOption,
  getTotalSignDocumentCount,
  groupBySigningMethod,
  AccountMenuLink,
} from "@/appBar/appBarDomain";
import { Avatar, AvatarButton } from "./Avatar";
import { Logo } from "./Logo";
import { isTestEnvironment } from "@/utils/environments";
import { TMessageKey } from "@/i18n/types.gen";
import { PortfolioSelector } from "@/portfolio/portfolioComponents";
import { signDocumentLabel, signingMethodLabel, supportsBatchSigning } from "@/common/common";
import { TMessage } from "@/i18n/tt";
import { useDispatch, useTranslation, useUserPrefs } from "@/stores/RootStoreContext";
import { updateUserPrefEvent } from "@/stores/userPrefsDomain";
import { resolveTheme, toggleThemePreference } from "@/theme/themeDomain";
import { preventDefault } from "@/utils/events";
import { SubscriptionType } from "@/common/subscription";
import { MenuBar } from "./MenuBar";
import { Alert } from "./Alert";
import { InfoButton } from "./InfoButton";
import { EventDispatcher, navigate } from "@/events/eventsDomain";
import { Popover as BasePopover } from "@base-ui/react/popover";
import { Markdown } from "@/text/Markdown.tsx";

const SubMenuLink = observer(function SubMenuLink({
  link: { url, text, badge, tag, isActive, badgeColor },
  className,
  ...tabProps
}: { link: AppBarSubLink } & TabElementProps) {
  const { t } = useTranslation();
  return (
    <a
      {...tabProps}
      href={url}
      className={classNames(
        className,
        "-outline-offset-4",
        "no-underline",
        "hover:bg-alt1",
        isActive
          ? "color-default visited:color-default hover:color-default"
          : "subtle visited:subtle hover:color-default",
      )}
    >
      <Box alignItems={"center"} gap={"sm"} whitespace={"nowrap"}>
        <span className={"link-text"}>{t(text)}</span>
        {tag && <Tag compact label={t(tag)} color={"error"} />}
        {badge && <BetaLabel label={badge} color={badgeColor} />}
      </Box>
    </a>
  );
});

function PortfolioSettings({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <a
      href={""}
      className={classNames(
        "p-2",
        "rounded-lg",
        "no-underline",
        "color-default",
        "active:color-default",
        "visited:color-default",
        "hover:color-default",
        "hover:bg-alt1",
      )}
      data-tooltip={t("portfolio.my-investments")}
      onClick={preventDefault(onClick)}
    >
      <Box alignItems={"center"} gap={"sm"} whitespace={"nowrap"}>
        <Icon name="gear" color={"info"} />
        {t("portfolio.portfolio-settings")}
      </Box>
    </a>
  );
}

function AccountMenu({ links, dispatch }: { links: AccountMenuLink[]; dispatch: EventDispatcher }) {
  const { t } = useTranslation();
  const open = (link: AccountMenuLink) => dispatch([navigate(link.url, { state: link.state })]);
  if (!links.length) return null;
  if (links.length === 1) {
    const [link] = links;
    return (
      <a
        href={link.url}
        onClick={preventDefault(() => open(link))}
        className={classNames(
          "p-2",
          "rounded-lg",
          "no-underline",
          "color-default",
          "active:color-default",
          "visited:color-default",
          "hover:color-default",
          "hover:bg-alt1",
        )}
      >
        <Box alignItems={"center"} gap={"sm"} whitespace={"nowrap"}>
          <Icon name={link.icon} />
          <div>{t(link.label)}</div>
        </Box>
      </a>
    );
  }
  return (
    <DropdownMenu
      button={{
        type: "link",
        color: "inherit",
        label: String(links.filter((link) => link.type === "enterprise-account").length),
        icon: "building",
      }}
      items={links.flatMap<DropdownMenuItemType>((link) => [
        ...(link.type === "superadmin" ? [{ type: "separator" } as const] : []),
        {
          type: "item",
          label: link.label,
          icon: link.icon,
          onSelect: () => open(link),
        },
      ])}
    />
  );
}

function HelpCenterLink({ url, icon, text, isActive }: AppBarLink) {
  const { t } = useTranslation();
  return (
    <a
      href={url}
      className={classNames(
        "p-2",
        "rounded-lg",
        "no-underline",
        "color-default",
        "active:color-default",
        "visited:color-default",
        "hover:color-default",
        "hover:bg-alt1",
        isActive && "bg-alt2-dimmed",
      )}
      data-tooltip={t(text)}
    >
      <Box alignItems={"center"} gap={"sm"} whitespace={"nowrap"}>
        <Icon name={icon} color={"info"} />
      </Box>
    </a>
  );
}

function AccessTag({ company }: { company: CompanyOption }) {
  const { t, lang } = useTranslation();
  return <Tag label={t(accessTagLabel(company, lang))} color={accessColors[company.accessType]} />;
}

const CompanyName = ({
  company,
  liquidationLabel,
  liquidationInfoText,
  mergedLabel,
  mergedInfoText,
  companyStatusLabel,
  companyStatusInfoText,
}: {
  company: CompanyOption;
  liquidationLabel?: TMessage;
  liquidationInfoText?: TMessage;
  mergedLabel?: TMessage;
  mergedInfoText?: TMessage;
  companyStatusLabel?: TMessage;
  companyStatusInfoText?: TMessage;
}) => {
  const { t } = useTranslation();
  // Liquidation and merger cessation are mutually exclusive; show whichever applies.
  const statusLabel = liquidationLabel ?? mergedLabel ?? companyStatusLabel;
  const statusInfoText = liquidationInfoText ?? mergedInfoText ?? companyStatusInfoText;
  return (
    <div className={"min-w-0"}>
      <div className={"font-semibold overflow-hidden text-ellipsis whitespace-nowrap"}>
        {company.name}
      </div>
      {statusLabel ? (
        <Box alignItems={"center"}>
          <div className={"color-error text-sm whitespace-nowrap"}>{t(statusLabel)}</div>
          {statusInfoText && <InfoButton infoText={statusInfoText} />}
        </Box>
      ) : (
        <div className={"text-sm overflow-hidden text-ellipsis whitespace-nowrap"}>
          {t("dashboard.orgNo")} {formatOrgNum(company.organisationNumber)}
        </div>
      )}
    </div>
  );
};

const CompanySelectorButton = forwardRef<
  HTMLButtonElement,
  {
    selectedCompany: Required<AppBarData>["selectedCompany"];
    isOpen: boolean;
    liquidationLabel?: TMessage;
    liquidationInfoText?: TMessage;
    mergedLabel?: TMessage;
    mergedInfoText?: TMessage;
    companyStatusLabel?: TMessage;
    companyStatusInfoText?: TMessage;
  } & ButtonHTMLAttributes<HTMLButtonElement>
>(function CompanySelectorButton(
  {
    selectedCompany,
    isOpen,
    className,
    liquidationLabel,
    liquidationInfoText,
    mergedLabel,
    mergedInfoText,
    companyStatusLabel,
    companyStatusInfoText,
    ...props
  },
  ref,
) {
  return (
    <button
      {...props}
      ref={ref}
      className={classNames(
        className,
        "-outline-offset-4",
        "flex",
        "items-center",
        "gap-2",
        "min-w-0",
      )}
    >
      <CompanyName
        company={selectedCompany}
        liquidationLabel={liquidationLabel}
        liquidationInfoText={liquidationInfoText}
        mergedLabel={mergedLabel}
        mergedInfoText={mergedInfoText}
        companyStatusLabel={companyStatusLabel}
        companyStatusInfoText={companyStatusInfoText}
      />
      <Icon name={isOpen ? "caret-up" : "caret-down"} />
    </button>
  );
});

function CompanySelector({
  selectedCompany,
  companies,
  subscription,
  actions,
  buttonClassName,
  liquidationLabel,
  liquidationInfoText,
  mergedLabel,
  mergedInfoText,
  companyStatusLabel,
  companyStatusInfoText,
  portfolioUrl,
  backToPortfolioLabel,
}: AppBarData & {
  buttonClassName?: string;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  if (!selectedCompany) {
    return null;
  }
  if (!companies) {
    return (
      <CompanyName
        company={selectedCompany}
        companyStatusLabel={companyStatusLabel}
        companyStatusInfoText={companyStatusInfoText}
      />
    );
  }
  return (
    <DropdownMenu
      button={({ isOpen, props }) => (
        <CompanySelectorButton
          className={buttonClassName}
          isOpen={isOpen}
          selectedCompany={selectedCompany}
          {...props}
          liquidationLabel={liquidationLabel}
          liquidationInfoText={liquidationInfoText}
          mergedLabel={mergedLabel}
          mergedInfoText={mergedInfoText}
          companyStatusLabel={companyStatusLabel}
          companyStatusInfoText={companyStatusInfoText}
        />
      )}
      items={[
        {
          type: "custom-item",
          render: ({ props }) => (
            <VBox htmlProps={props}>
              <Box padding>
                <a href={portfolioUrl} className="flex items-center gap-sm undecorated">
                  <Icon name={"caret-left"} />
                  {t(backToPortfolioLabel ?? { key: "backToPortfolio" })}
                </a>
              </Box>
              <Box alignItems={"center"} padding>
                <Box padding bg={"alt1"} rounded={"full"}>
                  <Icon name={"bank"} color={"info"} />
                </Box>
                <div>
                  <div>{selectedCompany.name}</div>
                  <div>
                    <small className={"subtle"}>
                      {formatOrgNum(selectedCompany.organisationNumber)}
                    </small>
                  </div>
                </div>
                <AccessTag company={selectedCompany} />
              </Box>
            </VBox>
          ),
          canSelect: false,
        },
        !subscription &&
          actions.openPaymentDialog && {
            type: "custom-item",
            canSelect: false,
            render: ({ props }) => (
              <VBox padding gap={"md"} htmlProps={props}>
                <div>{t("companyMenu.freemium.description")}</div>
                <VBox>
                  <Button
                    variant={"primary"}
                    fullWidth
                    label={t("companyMenu.freemium.becomeCustomer")}
                    onClick={() => {
                      if (actions.openPaymentDialog) {
                        dispatch(actions.openPaymentDialog);
                      }
                    }}
                  />
                  <small className={"subtle"}>{t("companyMenu.freemium.subDescription")}</small>
                </VBox>
              </VBox>
            ),
          },
        subscription?.subscriptionType === SubscriptionType.BASIC &&
          actions.upgradeToPro && {
            type: "custom-item",
            canSelect: false,
            render: ({ props }) => (
              <VBox padding gap={"md"} htmlProps={props}>
                <div>{t("companyMenu.basic.description")}</div>
                <VBox>
                  <Button
                    variant={"primary"}
                    fullWidth
                    label={t("companyMenu.basic.upgradeToPro")}
                    onClick={() => {
                      actions.upgradeToPro?.();
                    }}
                  />
                  <small className={"subtle"}>{t("companyMenu.basic.subDescription")}</small>
                </VBox>
              </VBox>
            ),
          },
        subscription?.subscriptionType !== SubscriptionType.PRO &&
          actions.showPricesLink && {
            type: "item",
            label: (
              <Box>
                <div>
                  <Markdown value={t("companyMenu.freemium.link-to-all-functions")} enableLinks />
                </div>
                <Icon name={"arrow-square-out"} />
              </Box>
            ),
            onSelect: noop,
          },
        selectedCompany.accessType === AccessType.ADMIN &&
          actions.openCompanySettings && {
            type: "item",
            label: t("companyMenu.links.companySettings"),
            onSelect: actions.openCompanySettings,
          },
        selectedCompany.accessType === AccessType.ADMIN &&
          actions.addAdministrator && {
            type: "item",
            label: t("companyMenu.links.addAdministrator"),
            onSelect: () =>
              actions.addAdministrator?.({
                organisationNumber: selectedCompany.organisationNumber,
                companyName: selectedCompany?.name ?? "",
              }),
          },
        selectedCompany.accessType === AccessType.ADMIN &&
          actions.showContactSaleAndSupport && {
            type: "item",
            label: (
              <Box>
                <div>
                  <Markdown value={t("companyMenu.freemium.link-to-contact-sales")} enableLinks />
                </div>
                <Icon name={"arrow-square-out"} />
              </Box>
            ),
            onSelect: noop,
          },
        companies && { type: "separator" },
        companies && { type: "label", label: t("companyMenu.changeToSubheader") },
        ...(companies ?? []).map<DropdownMenuItemType>((c) => ({
          type: "item",
          label: (
            <Box alignItems={"center"} justifyContent={"space-between"} grow>
              <div>{c.name}</div>
              <AccessTag company={c} />
            </Box>
          ),
          onSelect: () => actions.selectCompany?.(c),
        })),
      ]}
    />
  );
}

const accessLabelMappings: { [key in AccessType]: TMessageKey } = {
  [AccessType.ADMIN]: "accessType.ADMIN",
  [AccessType.SHAREHOLDER]: "accessType.SHAREHOLDER",
  [AccessType.ADDITIONAL_REPRESENTATIVE]: "accessType.ADDITIONAL_REPRESENTATIVE",
  [AccessType.BOARD_MEMBER]: "accessType.BOARD_MEMBER",
  [AccessType.SUPERADMIN]: "accessType.SUPERADMIN",
  [AccessType.NONE]: "accessType.NONE",
  [AccessType.DRAFT]: "accessType.DRAFT",
  [AccessType.CUSTOM]: "custom",
};

export const ThemeToggleButton = observer(function ThemeToggleButton() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userPrefs = useUserPrefs();
  const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  const resolved = resolveTheme(userPrefs?.theme, prefersDark);
  const next = resolved === "dark" ? "light" : "dark";
  return (
    <IconButton
      icon={resolved === "dark" ? "sun" : "moon"}
      tooltip={t(`appearance.switch-to-${next}`)}
      onClick={() =>
        dispatch(updateUserPrefEvent("theme", toggleThemePreference(userPrefs?.theme, prefersDark)))
      }
    />
  );
});

export const UserMenu = observer(function UserMenu({
  languages,
  user,
  selectedCompany,
  actions,
  className,
  userMainAction,
  disableUserContextInMenu,
}: AppBarData & {
  className?: string;
  disableUserContextInMenu?: boolean;
}) {
  const { t, lang } = useTranslation();
  return (
    <DropdownMenu
      alignment={"end"}
      button={({ props }) => (
        <AvatarButton {...props} className={className} textSize={"normal"} color={"primary"}>
          {user.initials}
        </AvatarButton>
      )}
      items={[
        !disableUserContextInMenu && {
          type: "custom-item",
          render: () => (
            <VBox padding gap={"md"}>
              <Box className={"min-w-[18rem]"} gap={"md"}>
                <Avatar textSize={"normal"}>{user.initials}</Avatar>
                <div>
                  <div>{user.name}</div>
                  {user.company && <div className={"text-sm subtle"}>{user.company}</div>}
                  {user.email && <div className={"text-sm subtle"}>{user.email}</div>}
                  {selectedCompany?.accessType && selectedCompany.accessType !== AccessType.NONE ? (
                    <div className={"mt-1"}>
                      <Tag
                        color={accessColors[selectedCompany.accessType]}
                        label={t(accessTagLabel(selectedCompany, lang, accessLabelMappings))}
                      />
                    </div>
                  ) : actions.logOut ? (
                    <div className={"text-sm subtle"}>{t("app-bar.logged-in")}</div>
                  ) : undefined}
                </div>
              </Box>
              {userMainAction}
            </VBox>
          ),
          canSelect: false,
        },
        (actions.logIn || actions.about) && { type: "separator" },
        actions.logIn && {
          type: "item",
          icon: "sign-in",
          label: t("landing.login"),
          onSelect: actions.logIn,
        },
        actions.about && {
          type: "item",
          icon: "question",
          label: t("landing.about-dcompany"),
          onSelect: actions.about,
        },
        actions.navigateToUserSettings && { type: "separator" },
        actions.navigateToUserSettings && {
          type: "item",
          label: t("settings.user"),
          icon: "gear",
          onSelect: actions.navigateToUserSettings,
        },
        selectedCompany?.accessType === AccessType.ADMIN &&
          actions.openPaymentDialog && {
            type: "item",
            label: t("header.subscriptions"),
            icon: "credit-card",
            onSelect: actions.openPaymentDialog,
          },
        !disableUserContextInMenu && actions.navigateToFeedback && { type: "separator" },
        actions.navigateToFeedback && { type: "label", label: t("help-us-improve") },
        actions.navigateToFeedback && {
          type: "item",
          label: t("give-us-feedback"),
          icon: "chat-text",
          onSelect: actions.navigateToFeedback,
        },
        languages.length && actions.changeLanguage && { type: "separator" },
        languages.length && actions.changeLanguage && { type: "label", label: t("language") },
        ...(actions.changeLanguage ? languages : []).map<DropdownMenuItemType>((l) => ({
          type: "radio-item",
          label: t(`language.${l.language}`),
          isSelected: l.language === lang,
          onSelect: () => {
            actions.changeLanguage?.(l.language);
          },
        })),
        actions.openCookieSettings && { type: "separator" },
        actions.openCookieSettings && {
          type: "item",
          label: t("cookies.cookie-settings"),
          onSelect: actions.openCookieSettings,
        },
        actions.logOut && { type: "separator" },
        actions.logOut && {
          type: "item",
          label: t("header.logout"),
          icon: "sign-out",
          onSelect: actions.logOut,
        },
      ]}
    />
  );
});

function headerClassName(props: { disableDropShadow?: boolean; borderBottom?: boolean }) {
  return classNames(
    !props.disableDropShadow
      ? "shadow-md"
      : props.borderBottom !== false
        ? "border-b border-gray"
        : undefined,
    "z-10",
  );
}

export function DesktopAppBar(
  props: AppBarData & {
    disableDropShadow?: boolean;
    subHeaderElement?: ReactElement;
    borderBottom?: boolean;
  },
) {
  const [headerRef, setHeaderRef] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const [showLabel, setShowLabel] = useState(
    () => isTestEnvironment() && !sessionStorage.getItem("hide-test-label"),
  );
  const dispatch = useDispatch();
  const hideTestLabel = useCallback(() => {
    sessionStorage.setItem("hide-test-label", "1");
    setShowLabel(false);
  }, []);

  const batchSignDocumentCounts = props.signDocumentCounts?.filter((c) =>
    supportsBatchSigning(c.signingMethod),
  );
  const totalSignDocumentCount = getTotalSignDocumentCount(batchSignDocumentCounts);
  return (
    <header ref={setHeaderRef} className={headerClassName(props)}>
      {showLabel && <TestEnvironmentMarker onClick={hideTestLabel} />}
      {props.notification && <Alert {...props.notification} />}
      <Box
        paddingX={"md"}
        paddingY={"sm"}
        alignItems={"center"}
        bg
        borderBottom={props.borderBottom !== false && (props.subLinks?.length ?? 0) > 0}
        gap={"md"}
      >
        <Logo
          linkUrl={props.portfolioUrl}
          tooltip={props.backToPortfolioLabel}
          variant={props.backToPortfolioLabel ? "back-button" : undefined}
        />
        {props.selectedCompany === undefined && <PortfolioSelector {...props} />}

        <Box grow overflow={"hidden"}>
          <CompanySelector
            {...props}
            buttonClassName={"grow shrink-1000 min-w-[15ch] max-w-[25ch]"}
          />
          {props.links && props.links.length > 0 && (
            <MenuBar items={props.links} containerRef={headerRef} />
          )}
        </Box>
        <Box marginLeft={"auto"} gap={"lg"} alignItems={"center"}>
          {renderBecomeCustomerButton(props) && (
            <Button
              variant={"primary"}
              label={t("header.become-customer")}
              onClick={props.actions.openPaymentDialog}
              size={"sm"}
            />
          )}
          {renderUpgradeSubscriptionButton(props) && (
            <Button
              label={t("header.upgrade")}
              onClick={props.actions.openPaymentDialog}
              variant={"secondary"}
              size={"sm"}
            />
          )}
          {props.helpCenter && (
            <Box paddingX={"sm"}>
              <HelpCenterLink {...props.helpCenter} />
            </Box>
          )}
          {props.actions.openPortfolioSettings && (
            <PortfolioSettings onClick={props.actions.openPortfolioSettings} />
          )}
          {totalSignDocumentCount > 0 && props.actions.openBatchSigningPage && (
            <DropdownMenu
              button={{
                type: "icon",
                icon: "pen",
                tag: String(totalSignDocumentCount),
              }}
              items={groupBySigningMethod(batchSignDocumentCounts).flatMap(
                ({ signingMethod, count }) => {
                  const label = signingMethodLabel(signingMethod)!;
                  return [
                    {
                      type: "label",
                      label: <Box whitespace={"nowrap"}>{t(label)}</Box>,
                    },
                    {
                      type: "item",
                      label: <Box whitespace={"nowrap"}>{t(signDocumentLabel(count))}</Box>,
                      icon: "pen",
                      onSelect: () => props.actions.openBatchSigningPage?.(signingMethod),
                    },
                  ];
                },
              )}
            />
          )}
          {props.displayUserName && (
            <div className="text-right">
              <div className="font-semibold">{props.user.name}</div>
              {props.user.email && <div className="text-sm">{props.user.email}</div>}
            </div>
          )}
          {props.accountMenuLinks && (
            <AccountMenu links={props.accountMenuLinks} dispatch={dispatch} />
          )}
          <ThemeToggleButton />
          <UserMenu {...props} />
        </Box>
      </Box>
      {props.subHeaderElement}
      {props.subLinks && (
        <Box display={"block"} bg>
          <Tabs
            tabs={props.subLinks.map((l) => ({
              id: l.url,
              render: (props) => <SubMenuLink link={l} {...props} />,
              onSelect: noop,
              isActive: l.isActive,
            }))}
          />
        </Box>
      )}
    </header>
  );
}

function MobileMenu(props: AppBarData) {
  const { t } = useTranslation();
  return (
    <VBox padding gap={"md"}>
      {props.links?.map((l) => (
        <VBox key={l.url}>
          <a
            href={l.url}
            className={classNames(
              "no-underline",
              "color-default",
              "visited:color-default",
              "active:color-default",
              l.isActive && "font-bold",
            )}
          >
            <Box alignItems={"center"} textSize={"lg"}>
              <Icon name={l.icon} color={l.isActive ? "info" : "default"} />
              <span>{t(l.text)}</span>
              {l.tag && (
                <Tag
                  compact
                  label={t(l.tag)}
                  color={l.tagColor ?? "info"}
                  className={l.isActive ? "font-normal" : undefined}
                />
              )}
              {l.badge && <BetaLabel label={l.badge} color={l.badgeColor} />}
            </Box>
          </a>
          {!isEmpty(l.subLinks) && (
            <VBox alignItems={"start"}>
              {l.subLinks?.map((sub) => (
                <a
                  key={sub.url}
                  href={sub.url}
                  className={classNames(
                    "ml-6",
                    "no-underline",
                    sub.isActive
                      ? ["color-default", "visited:color-default", "active:color-default"]
                      : ["subtle", "visited:subtle", "active:subtle"],
                  )}
                >
                  <Box alignItems={"center"}>
                    <span>{t(sub.text)}</span>
                    {sub.badge && <BetaLabel label={sub.badge} color={sub.badgeColor} />}
                    {sub.tag && <Tag label={sub.tag} color={"error"} />}
                  </Box>
                </a>
              ))}
            </VBox>
          )}
        </VBox>
      ))}
    </VBox>
  );
}

export function MobileAppBar(
  props: AppBarData & { disableDropShadow?: boolean; subHeaderElement?: ReactElement },
) {
  const { t } = useTranslation();
  const [headerRef, setHeaderRef] = useState<HTMLElement | null>(null);

  return (
    <BasePopover.Root>
      <header ref={setHeaderRef} className={headerClassName(props)}>
        <Box className={styles.mobileHeader} gap={false} bg overflow={"hidden"} padding={"sm"}>
          {isEmpty(props.links) ? (
            <Box gap>
              <Logo linkUrl={props.portfolioUrl} />
              <Box>{props.selectedCompany === undefined && <PortfolioSelector {...props} />}</Box>
            </Box>
          ) : (
            <BasePopover.Trigger
              render={(props, state) => (
                <IconButton
                  {...(props as IconButtonProps)}
                  icon={state.open ? "x" : "list"}
                  tooltip={t("open-menu")}
                />
              )}
            ></BasePopover.Trigger>
          )}
          <CompanySelector {...props} buttonClassName={"mx-auto text-center"} />
          <Box className={"ml-auto"} alignItems={"center"} gap={"sm"}>
            <ThemeToggleButton />
            <UserMenu {...props} />
          </Box>
        </Box>
        {props.subHeaderElement}
      </header>
      <BasePopover.Portal>
        <BasePopover.Positioner anchor={headerRef}>
          <BasePopover.Popup
            className={"overflow-auto border-t border-gray-soft bg-default"}
            style={{ width: "var(--available-width)", height: "var(--available-height)" }}
          >
            <MobileMenu {...props} />
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}

export function AppBar({
  data,
  disableDropShadow,
  subHeaderElement,
  borderBottom,
}: {
  data: AppBarData | null;
  disableDropShadow?: boolean;
  subHeaderElement?: ReactElement;
  borderBottom?: boolean;
}) {
  if (!data) {
    return null;
  }
  return isMobileView() ? (
    <MobileAppBar
      {...data}
      disableDropShadow={disableDropShadow}
      subHeaderElement={subHeaderElement}
    />
  ) : (
    <DesktopAppBar
      {...data}
      disableDropShadow={disableDropShadow}
      subHeaderElement={subHeaderElement}
      borderBottom={borderBottom}
    />
  );
}

function renderBecomeCustomerButton(props: AppBarData & { disableDropShadow?: boolean }) {
  const hasActiveSubsubscription = props.subscription?.isActive;
  const hasSelectedCompanyAndIsAdmin =
    props.selectedCompany && props.selectedCompany?.accessType === AccessType.ADMIN;
  return (
    !hasActiveSubsubscription && hasSelectedCompanyAndIsAdmin && props.actions.openPaymentDialog
  );
}

function renderUpgradeSubscriptionButton(props: AppBarData & { disableDropShadow?: boolean }) {
  return (
    props.subscription?.subscriptionType === SubscriptionType.BASIC &&
    props.subscription.subscriptionType.valueOf() !== "SHAREHOLDER_REGISTER" &&
    props.selectedCompany &&
    props.selectedCompany?.accessType === AccessType.ADMIN &&
    props.actions.openPaymentDialog
  );
}

function TestEnvironmentMarker({ onClick }: { onClick: () => void }) {
  return (
    <div
      style={{
        position: "absolute",
        width: "100px",
        paddingLeft: "20px",
        height: "60px",
      }}
      onClick={onClick}
    >
      <span
        style={{
          position: "absolute",
          fontSize: "10px",
          background: "#5C6A8FFF",
          color: "#fff",
          padding: "17px 40px",
          marginTop: "-10px",
          marginLeft: "10px",
          marginRight: "10px",
          fontWeight: "bold",
          transform: "translate(-60%,0) rotate(-45deg)",
          transformOrigin: "center",
          opacity: "60%",
        }}
      >
        TEST
      </span>
    </div>
  );
}
